import React from 'react';
import { Link } from 'react-router-dom';
import './PolicyPage.css';

const GDPRCompliance: React.FC = () => {
    return (
        <div className="policy-page">
            <nav className="policy-nav">
                <Link to="/" className="logo">Commerium</Link>
            </nav>

            <main className="policy-content">
                <h1>GDPR Compliance</h1>
                <p className="last-updated">Last updated: January 20, 2026</p>

                <section>
                    <h2>1. Our Commitment to GDPR</h2>
                    <p>
                        Commerium is committed to protecting the privacy and security of your personal data in
                        compliance with the General Data Protection Regulation (GDPR). This page outlines how we
                        meet our obligations under the GDPR.
                    </p>
                </section>

                <section>
                    <h2>2. Data Controller Information</h2>
                    <p>
                        <strong>Data Controller:</strong> Commerium Technologies S.L.<br />
                        <strong>Address:</strong> Calle Mayor 1, 28001 Madrid, Spain<br />
                        <strong>Email:</strong> privacy@trycommerium.com<br />
                        <strong>Data Protection Officer:</strong> dpo@trycommerium.com
                    </p>
                </section>

                <section>
                    <h2>3. Lawful Basis for Processing</h2>
                    <p>We process personal data based on the following lawful bases:</p>

                    <h3>3.1 Contract Performance (Article 6(1)(b))</h3>
                    <ul>
                        <li>Providing our Platform services</li>
                        <li>Processing payments and subscriptions</li>
                        <li>Managing your account</li>
                        <li>Connecting to third-party marketplaces</li>
                    </ul>

                    <h3>3.2 Legitimate Interests (Article 6(1)(f))</h3>
                    <ul>
                        <li>Improving our Platform and services</li>
                        <li>Detecting and preventing fraud</li>
                        <li>Ensuring network and information security</li>
                        <li>Sending service-related communications</li>
                    </ul>

                    <h3>3.3 Consent (Article 6(1)(a))</h3>
                    <ul>
                        <li>Marketing communications (opt-in)</li>
                        <li>Non-essential cookies</li>
                        <li>Processing sensitive data (if applicable)</li>
                    </ul>

                    <h3>3.4 Legal Obligation (Article 6(1)(c))</h3>
                    <ul>
                        <li>Tax and accounting requirements</li>
                        <li>Responding to legal requests</li>
                        <li>Fraud prevention obligations</li>
                    </ul>
                </section>

                <section>
                    <h2>4. Your Rights Under GDPR</h2>
                    <p>As a data subject, you have the following rights:</p>

                    <div className="rights-grid">
                        <div className="right-card">
                            <h4>📋 Right of Access (Article 15)</h4>
                            <p>Request a copy of your personal data we hold. We will provide this within 30 days.</p>
                        </div>
                        <div className="right-card">
                            <h4>✏️ Right to Rectification (Article 16)</h4>
                            <p>Request correction of inaccurate or incomplete personal data.</p>
                        </div>
                        <div className="right-card">
                            <h4>🗑️ Right to Erasure (Article 17)</h4>
                            <p>Request deletion of your personal data ("right to be forgotten").</p>
                        </div>
                        <div className="right-card">
                            <h4>⏸️ Right to Restriction (Article 18)</h4>
                            <p>Request limitation of processing in certain circumstances.</p>
                        </div>
                        <div className="right-card">
                            <h4>📦 Right to Portability (Article 20)</h4>
                            <p>Receive your data in a structured, machine-readable format.</p>
                        </div>
                        <div className="right-card">
                            <h4>🚫 Right to Object (Article 21)</h4>
                            <p>Object to processing based on legitimate interests or for direct marketing.</p>
                        </div>
                    </div>

                    <p>
                        To exercise any of these rights, email us at <strong>privacy@trycommerium.com</strong>.
                        We will respond within 30 days. No fee is charged for the first request.
                    </p>
                </section>

                <section>
                    <h2>5. Data Processing Activities</h2>
                    <table className="cookie-table">
                        <thead>
                            <tr>
                                <th>Activity</th>
                                <th>Data Processed</th>
                                <th>Legal Basis</th>
                                <th>Retention</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Account Registration</td>
                                <td>Name, email, password</td>
                                <td>Contract</td>
                                <td>Account lifetime + 30 days</td>
                            </tr>
                            <tr>
                                <td>AI Listing Generation</td>
                                <td>Product images, descriptions</td>
                                <td>Contract</td>
                                <td>Until deleted by user</td>
                            </tr>
                            <tr>
                                <td>Payment Processing</td>
                                <td>Billing info (via Shopify)</td>
                                <td>Contract</td>
                                <td>7 years (legal requirement)</td>
                            </tr>
                            <tr>
                                <td>Analytics</td>
                                <td>Anonymous usage data</td>
                                <td>Legitimate Interest</td>
                                <td>26 months</td>
                            </tr>
                            <tr>
                                <td>Marketing Emails</td>
                                <td>Email address</td>
                                <td>Consent</td>
                                <td>Until unsubscribed</td>
                            </tr>
                        </tbody>
                    </table>
                </section>

                <section>
                    <h2>6. International Data Transfers</h2>
                    <p>
                        Your data may be transferred to and processed in countries outside the EEA. We ensure
                        appropriate safeguards through:
                    </p>
                    <ul>
                        <li><strong>Adequacy Decisions:</strong> Transfers to countries with adequate data protection</li>
                        <li><strong>Standard Contractual Clauses (SCCs):</strong> EU-approved contracts with processors</li>
                        <li><strong>Data Processing Agreements:</strong> Binding contracts with all sub-processors</li>
                    </ul>

                    <h3>Our Sub-Processors:</h3>
                    <ul>
                        <li><strong>Render (US):</strong> Hosting - SCCs in place</li>
                        <li><strong>Vercel (US):</strong> Backend hosting - SCCs in place</li>
                        <li><strong>Supabase (US/EU):</strong> Database - EU region available</li>
                        <li><strong>Shopify (US/EU):</strong> Payments - processed via Shopify Billing API</li>
                        <li><strong>Google Cloud (EU):</strong> AI processing - EU region</li>
                    </ul>
                </section>

                <section>
                    <h2>7. Data Security</h2>
                    <p>We implement technical and organizational measures including:</p>
                    <ul>
                        <li>AES-256 encryption for data at rest</li>
                        <li>TLS 1.3 encryption for data in transit</li>
                        <li>Regular security audits and penetration testing</li>
                        <li>Access controls and authentication</li>
                        <li>Employee training on data protection</li>
                        <li>Incident response procedures</li>
                        <li>Data backup and recovery systems</li>
                    </ul>
                </section>

                <section>
                    <h2>8. Data Breach Notification</h2>
                    <p>
                        In the event of a personal data breach that poses a risk to your rights and freedoms,
                        we will:
                    </p>
                    <ul>
                        <li>Notify the Spanish Data Protection Agency (AEPD) within 72 hours</li>
                        <li>Inform affected individuals without undue delay</li>
                        <li>Document the breach and response actions</li>
                        <li>Implement measures to prevent recurrence</li>
                    </ul>
                </section>

                <section>
                    <h2>9. Data Protection Impact Assessments</h2>
                    <p>
                        We conduct Data Protection Impact Assessments (DPIAs) for processing activities that
                        may result in high risk to individuals, including:
                    </p>
                    <ul>
                        <li>Introduction of new AI processing features</li>
                        <li>New third-party integrations</li>
                        <li>Changes to data storage or processing locations</li>
                    </ul>
                </section>

                <section>
                    <h2>10. Complaints</h2>
                    <p>
                        If you believe your data protection rights have been violated, you have the right to
                        lodge a complaint with a supervisory authority. In Spain, this is:
                    </p>
                    <p>
                        <strong>Agencia Española de Protección de Datos (AEPD)</strong><br />
                        C/ Jorge Juan 6, 28001 Madrid<br />
                        Website: <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">www.aepd.es</a>
                    </p>
                    <p>
                        However, we encourage you to contact us first so we can address your concerns directly.
                    </p>
                </section>

                <section>
                    <h2>11. Contact Our DPO</h2>
                    <p>
                        For any questions regarding GDPR compliance or to exercise your rights, contact our
                        Data Protection Officer:
                    </p>
                    <ul>
                        <li>Email: dpo@trycommerium.com</li>
                        <li>Address: Commerium Technologies, Calle Mayor 1, 28001 Madrid, Spain</li>
                    </ul>
                </section>
            </main>

            <footer className="policy-footer">
                <Link to="/">← Back to Home</Link>
                <p>© 2026 Commerium. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default GDPRCompliance;
